import React, { useMemo, useCallback, useState } from 'react';
import { Trans } from '@lingui/macro';
import { useMutation, useQuery } from '@apollo/client';
import { useLingui } from '@lingui/react';
import { Modal } from 'antd';

import { Query, QueryFetchCandidatePositionRelationsForMeArgs } from 'types';
import { submittedStatus } from 'config/AppConfig';
import {
  QUERY_CANDIDATE_REFERENCER_RELATIONS_FOR_ME,
  CREATE_CANDIDATESHARE_INVITE,
  RETURN_CANDIDATESHARE_INVITES_FOR_CANDIDATE,
} from 'api/candidate_shares_api';
import { FETCH_APPLIED_COMPANIES_FOR_ME } from 'api/candidate_api';
import SplashScreen from '../SplashScreen';
import CandidateShareStepper from '../StepperFlow/CandidateShareStepper';
import { getNetworkError } from 'util/utilFunctions';
import SuccessWithUpsellModal from './CandidateExperienceV1/MySearchlightV2/components/SuccessModal';

interface CandidateSharePageNewInviteModalProps {
  isVisible: boolean;
  setIsVisible(isVisible: boolean): void;
}

const CandidateSharePageNewInviteModal = ({
  isVisible,
  setIsVisible,
}: CandidateSharePageNewInviteModalProps): JSX.Element => {
  const { i18n } = useLingui();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [inviteeName, setInviteeName] = useState('');
  const { data: sharedData } = useQuery<Pick<Query, 'fetchCandidateShareInvitesForMe'>>(
    RETURN_CANDIDATESHARE_INVITES_FOR_CANDIDATE
  );
  const { data: appliedCompaniesData } = useQuery<
    Pick<Query, 'fetchCandidatePositionRelationsForMe'>,
    QueryFetchCandidatePositionRelationsForMeArgs
  >(FETCH_APPLIED_COMPANIES_FOR_ME, {
    variables: {
      input: {
        isArchived: false,
      },
    },
  });
  const { loading, error, data } = useQuery<Pick<Query, 'candidateReferencerRelationsForMe'>>(
    QUERY_CANDIDATE_REFERENCER_RELATIONS_FOR_ME
  );
  const [createCandidateShareInviteCallback] = useMutation(CREATE_CANDIDATESHARE_INVITE);
  const createShare = useCallback(
    async newShare => {
      try {
        await createCandidateShareInviteCallback({
          variables: { input: newShare },
          refetchQueries: [RETURN_CANDIDATESHARE_INVITES_FOR_CANDIDATE],
        });
        setInviteeName(newShare.inviteeFirstName);
        setShowSuccessModal(true);
      } catch (err) {
        console.error(err);
        alert(getNetworkError(err));
      }
    },
    [createCandidateShareInviteCallback]
  );

  const validateAndCreate = useCallback(
    async values => {
      const newShare = {
        inviteeFirstName: values.sharingInformation.firstName,
        inviteeLastName: values.sharingInformation.lastName,
        inviteeCompany: values.sharingInformation.company,
        inviteeEmail: values.sharingInformation.workEmail,
        sharedCandidateReferencerRelations: values.selectedReferences.map(
          (item: { key: string }) => ({
            candidateReferencerRelationId: +item.key,
          })
        ),
      };
      await createShare(newShare);
    },
    [createShare]
  );

  const references = useMemo(() => {
    if (data?.candidateReferencerRelationsForMe) {
      return data.candidateReferencerRelationsForMe
        .filter(
          item =>
            (item.referenceRequests || []).filter(
              req => req.statusType.typeName === submittedStatus
            ).length > 0
        )
        .map(item => ({
          key: +item.candidateReferencerRelationId,
          subtitle: i18n._(/*i18n*/ { id: item.workRelationship, message: item.workRelationship }),
          title: `${item.referencerFirstName} ${item.referencerLastName}`,
        }));
    }
    return [];
  }, [i18n, data]);

  if (loading || !sharedData || !appliedCompaniesData || error) {
    if (error) alert(`Error!: ${error}`);

    return <SplashScreen />;
  }
  return showSuccessModal ? (
    <SuccessWithUpsellModal
      successText="You have granted access to your Searchlight report!"
      nextSteps={[
        `${inviteeName} will be notified that access has been granted`,
        `${inviteeName} will get insights to see the whole you beyond your resume.`,
        `${inviteeName} may choose to reach out to you directly for questions or next steps.`,
      ]}
      isVisible={showSuccessModal}
      onClose={() => {
        setShowSuccessModal(false);
        setIsVisible(false);
      }}
    />
  ) : (
    <Modal
      className="tw-w-[560px]"
      visible={isVisible}
      onCancel={() => setIsVisible(false)}
      footer={null}
    >
      <CandidateShareStepper
        references={references}
        onSubmit={validateAndCreate}
        mainTitle={<Trans>Share your references</Trans>}
        sharedData={sharedData.fetchCandidateShareInvitesForMe || []}
        appliedCompanies={appliedCompaniesData.fetchCandidatePositionRelationsForMe}
      />
    </Modal>
  );
};

export default CandidateSharePageNewInviteModal;
